import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller, useWatch } from "react-hook-form";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import {
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  Paper,
  Typography,
  Divider,
  Chip,
} from "@mui/material";
import InputData from "../../shared/components/formulario/input-data/inputData";
import InputMascarado from "../../shared/components/formulario/input-mascarado/InputMascarado";
import { mascaraMoedaReal } from "../../shared/utils/mascaras/mascaraMoedaReal";
import { TextField, SelectField } from "../../shared/components/ui";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DiscountTypeEnum } from "../../shared/utils/enum/enum";
import { contasAReceberService } from "./service/criar-contas-a-receber.service";
import { useMutation } from "@tanstack/react-query";
import { toastError } from "../../shared/services/errors/erros-handler";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import { SelectBancosEmpresa } from "../../shared/components/formulario/input-select-bancos-empres/selectBancoEmpresa";
import { SelectAgenciaEmpresa } from "../../shared/components/formulario/input-select-agencia/selectAgencias";
import { SelectContaEmpresa } from "../../shared/components/formulario/input-select-conta-bancaria/selectConta";
import { useEffect, useMemo } from "react";
import { LiquidarContasAReceberSchema, LiquidarContasReceberFormData } from "./schema/liquidar-conta-a-receber.schema";
import { InputSwitch } from "../../shared/components/formulario/input-switch/switch";

function formatCurrency(v: number) {
  return v.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });
}

export function LiquidarContaAReceber() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const parcela = location.state?.parcela;

  const methods = useForm<LiquidarContasReceberFormData>({
    resolver: yupResolver(LiquidarContasAReceberSchema),
    defaultValues: {
      paymentDate: new Date(),
      additionValue: 0,
      discountType: undefined,
      discountValue: 0,
      isMovimentToBank: true,
      idBank: "",
      idAgency: "",
      idAccount: "",
    } satisfies Omit<LiquidarContasReceberFormData, "paidValue">, // Garante tipagem correta,
    mode: "onChange",
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch, // Importe a função watch
  } = methods;

  // Watch fields
  const additionValue = watch("additionValue") || 0;
  const discountType = watch("discountType");
  const discountValue = watch("discountValue") || 0;
  const isMovimentToBank = watch("isMovimentToBank");

  // Base value (parcela original)
  const valorBase = useMemo(
    () => Number(parcela?.value || 0),
    [parcela?.value]
  );

  // Remova este useEffect, pois ele está sobrescrevendo os valores dos inputs
  /*useEffect(() => {
    setValue("discountValue", discountValue);
    setValue("additionValue", additionValue);
  }, [valorBase, additionValue, discountType, discountValue]);*/

  // Cálculo memoizado
  const valorFinal = useMemo(() => {
    const base = valorBase;
    const add = converValue(additionValue);
    const discVal = converValue(discountValue);
    let total = base + add;

    if (discountType === DiscountTypeEnum.PERCENTAGE) {
      total -= (base * discVal) / 100;
    } else if (discountType === DiscountTypeEnum.FIXED) {
      total -= discVal;
    }

    if (!Number.isFinite(total)) total = 0;
    if (total < 0) total = 0;
    return Number(total.toFixed(2));
  }, [valorBase, additionValue, discountType, discountValue]);

  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));

  const { isPending, mutate } = useMutation({
    mutationFn: async (
      data: Omit<LiquidarContasReceberFormData, "paidValue"> & {
        paidValue: number;
      }
    ) => {
      const result = await contasAReceberService.liquidar(id || "", {
        ...data,
        paidValue: valorFinal,
      });
      return result;
    },
    onSuccess: () => {
      toast.success("Conta liquidada com sucesso!");
      navigate("/financeiro/contas-a-receber");
    },
    onError: (err: any) => {
      toastError(err);
    },
  });

  return (
    <BaseLayout
      title={`Liquidar Parcela do Documento - ${parcela.description || "Parcela"
        }`}
      barraDeFerramentas={
        <FerramanetasDetalhe
          mostraBotaoApagar={false}
          mostraBotaoNovo={false}
          mostraBotaoSalvarEVoltar={false}
          mostraBotaoVoltar
          textBotaoSalvar="Liquidar"
          aoClicarBotaoSalvar={handleSubmit((formData) => mutate(formData))}
          aoClicarBotaoVoltar={() => navigate("/financeiro/contas-a-receber")}
        />
      }
    >
      <Box component="form" onSubmit={handleSubmit((d) => mutate(d))} p={1}>
        <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
          <Typography paddingBottom={2} variant="subtitle2" gutterBottom>
            Ajustes de Pagamento
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Controller
                name="paymentDate"
                control={control}
                render={({ field }) => (
                  <InputData
                    {...field}
                    name="paymentDate"
                    methods={methods}
                    label="Data do Pagamento"
                    error={!!errors.paymentDate}
                    helperText={errors.paymentDate?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Valor Pago (Calculado)"
                value={formatCurrency(valorFinal)}
                readOnly
                disabled
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <InputMascarado
                name="additionValue"
                methods={methods}
                label="Acréscimo"
                mask={mascaraMoedaReal}
                error={!!errors.additionValue}
                helperText={errors.additionValue?.message}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                name="discountType"
                control={control}
                render={({ field }) => (
                  <SelectField
                    {...field}
                    label="Tipo de Desconto"
                    error={!!errors.discountType}
                    helperText={errors.discountType?.message}
                    value={field.value ?? ""}
                  >
                    <option value="">Nenhum</option>
                    <option value={DiscountTypeEnum.PERCENTAGE}>
                      Percentual
                    </option>
                    <option value={DiscountTypeEnum.FIXED}>Valor</option>
                  </SelectField>
                )}
              />
            </Grid>
            {discountType && (
              <Grid item xs={12} md={3}>
                <InputMascarado
                  name="discountValue"
                  methods={methods}
                  label="Valor do Desconto"
                  mask={mascaraMoedaReal}
                  error={!!errors.discountValue}
                  helperText={errors.discountValue?.message}
                />
              </Grid>
            )}
            <Grid
              item
              xs={12}
              md={discountType ? 9 : 12}
              display="flex"
              alignItems="center"
            >
              <InputSwitch
                name="isMovimentToBank"
                label="Esta movimentação está relacionada a um banco?"
                methods={methods}
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Box
            display="flex"
            flexWrap="wrap"
            gap={1.5}
            alignItems="center"
            justifyContent="flex-start"
          >
            <Chip
              color="default"
              label={`Valor Base: ${formatCurrency(valorBase)}`}
              size="small"
            />
            <Chip
              color={additionValue ? "primary" : "default"}
              label={`Acréscimo: ${formatCurrency(converValue(additionValue))}`}
              size="small"
            />
            <Chip
              color={discountValue ? "warning" : "default"}
              label={`Desconto: ${discountType === DiscountTypeEnum.PERCENTAGE
                  ? `${converValue(discountValue)}%`
                  : formatCurrency(converValue(discountValue))
                }`}
              size="small"
            />
            <Chip
              color="success"
              label={`Valor Final: ${formatCurrency(valorFinal)}`}
              size="small"
            />
          </Box>
        </Paper>

        {isMovimentToBank && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Dados Bancários
            </Typography>
            <Box
              display="grid"
              gap={2}
              gridTemplateColumns={{
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1fr 1fr 1fr",
              }}
            >
              <SelectBancosEmpresa
                name="idBank"
                methods={methods}
                label="Banco"
                required
              />
              <SelectAgenciaEmpresa
                name="idAgency"
                methods={methods}
                idBank={methods.watch("idBank")}
                required
              />
              <SelectContaEmpresa
                name="idAccount"
                methods={methods}
                idAgency={methods.watch("idAgency")}
                required
              />
            </Box>
          </Paper>
        )}
      </Box>

      {parcela && (
        <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Informações da Parcela
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Valor Original
              </Typography>
              <Typography variant="body2">
                {formatCurrency(Number(parcela.value || 0))}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Typography variant="body2">
                {(() => {
                  // Verifica se está atrasado (status pending e data vencimento menor que hoje)
                  const isOverdue =
                    parcela.status === "pending" &&
                    parcela.dueDate &&
                    new Date(parcela.dueDate) < new Date();

                  if (isOverdue) {
                    return (
                      <Chip label="Em atraso" color="error" size="small" />
                    );
                  }

                  switch (parcela.status) {
                    case "pending":
                      return (
                        <Chip
                          label="Aguardando pagamento"
                          color="info"
                          size="small"
                        />
                      );
                    case "paid":
                      return <Chip label="Paga" color="success" size="small" />;
                    case "overdue":
                      return (
                        <Chip label="Atrasada" color="error" size="small" />
                      );
                    case "anticipated":
                      return (
                        <Chip label="Antecipada" color="primary" size="small" />
                      );
                    default:
                      return (
                        <Chip
                          label={parcela.status}
                          color="default"
                          size="small"
                        />
                      );
                  }
                })()}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Vencimento
              </Typography>
              <Typography variant="body2">
                {parcela.dueDate
                  ? new Date(parcela.dueDate).toLocaleDateString("pt-BR")
                  : "-"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}
    </BaseLayout>
  );
}

function converValue(string: any) {
  const val =
    Number(
      String(string)
        .replace(/[^0-9,-]+/g, "")
        .replace(",", ".")
    ) || 0;

  return val;
}